/**
 * Helpers for event links, rules and learning (Events hub Stage 1B): fallback names and colours
 * (when GET api/events/link-lookups fails), grouping for the Connected tab and the rule signal
 * value pickers.
 *
 * Signal value keys sent to the API: tag id, tag group id, type id, campus (location) id, venue id
 * and source id as text; title keywords as typed; "true" / "false" for Public and All campuses;
 * "0" (Sunday) to "6" (Saturday) for the day of the week; morning / afternoon / evening / allday for
 * the time band.
 */

import {
  EventLinkOriginIds,
  EventLinkRoleIds,
  EventLinkStatusIds,
  EventLinkTypeIds,
  EventRuleActionTypeIds,
  EventRuleCandidateStatusIds,
  EventRuleOperatorIds,
  EventSignalTypeIds,
  type EventLinkView,
} from '@actschurch/ccms-sdk';

// ---------------------------------------------------------------- names + colours

export const LINK_TYPE_NAMES: Record<number, string> = {
  [EventLinkTypeIds.Department]: 'Department',
  [EventLinkTypeIds.Designation]: 'Designation',
  [EventLinkTypeIds.Ministry]: 'Ministry',
  [EventLinkTypeIds.MinistryTeam]: 'Ministry team',
  [EventLinkTypeIds.Venue]: 'Venue',
  [EventLinkTypeIds.Location]: 'Campus',
  [EventLinkTypeIds.Person]: 'Person',
  [EventLinkTypeIds.BookableResource]: 'Bookable resource',
  [EventLinkTypeIds.Asset]: 'Asset',
  [EventLinkTypeIds.Task]: 'Task',
  [EventLinkTypeIds.Project]: 'Project',
  [EventLinkTypeIds.Meeting]: 'Meeting',
  [EventLinkTypeIds.Booking]: 'Booking',
  [EventLinkTypeIds.Budget]: 'Budget',
  [EventLinkTypeIds.PurchaseRequest]: 'Purchase request',
  [EventLinkTypeIds.Announcement]: 'Announcement',
  [EventLinkTypeIds.Campaign]: 'Campaign',
  [EventLinkTypeIds.Form]: 'Form',
};

/** Link types a person can pick in 1B (the ones the server resolves names for). */
export const PICKABLE_LINK_TYPES: number[] = [
  EventLinkTypeIds.Department,
  EventLinkTypeIds.Designation,
  EventLinkTypeIds.Ministry,
  EventLinkTypeIds.MinistryTeam,
  EventLinkTypeIds.Venue,
  EventLinkTypeIds.Location,
  EventLinkTypeIds.Person,
];

/** Link types the add-link drawer offers: the 1B types plus Stage 3's manual Campaign and Form links. */
export const ADD_LINK_TYPES: number[] = [...PICKABLE_LINK_TYPES, EventLinkTypeIds.Campaign, EventLinkTypeIds.Form];

export const LINK_TYPE_COLORS: Record<number, string> = {
  [EventLinkTypeIds.Department]: 'blue',
  [EventLinkTypeIds.Designation]: 'indigo',
  [EventLinkTypeIds.Ministry]: 'grape',
  [EventLinkTypeIds.MinistryTeam]: 'violet',
  [EventLinkTypeIds.Venue]: 'orange',
  [EventLinkTypeIds.Location]: 'orange',
  [EventLinkTypeIds.Person]: 'teal',
  [EventLinkTypeIds.Budget]: 'green',
  [EventLinkTypeIds.PurchaseRequest]: 'yellow',
  [EventLinkTypeIds.Announcement]: 'indigo',
  [EventLinkTypeIds.Campaign]: 'pink',
  [EventLinkTypeIds.Form]: 'cyan',
};

export const ROLE_NAMES: Record<number, string> = {
  [EventLinkRoleIds.Involved]: 'Involved',
  [EventLinkRoleIds.Lead]: 'Lead',
  [EventLinkRoleIds.Supporting]: 'Supporting',
  [EventLinkRoleIds.Related]: 'Related',
};
export const ROLE_COLORS: Record<number, string> = {
  [EventLinkRoleIds.Involved]: 'gray',
  [EventLinkRoleIds.Lead]: 'blue',
  [EventLinkRoleIds.Supporting]: 'cyan',
  [EventLinkRoleIds.Related]: 'gray',
};

export const ORIGIN_NAMES: Record<number, string> = {
  [EventLinkOriginIds.Manual]: 'Manual',
  [EventLinkOriginIds.Rule]: 'Rule',
  [EventLinkOriginIds.LearnedRule]: 'Learned rule',
  [EventLinkOriginIds.Ai]: 'AI',
  [EventLinkOriginIds.Playbook]: 'Playbook',
  [EventLinkOriginIds.SourceTag]: 'Source tag',
};
export const ORIGIN_COLORS: Record<number, string> = {
  [EventLinkOriginIds.Manual]: 'gray',
  [EventLinkOriginIds.Rule]: 'blue',
  [EventLinkOriginIds.LearnedRule]: 'violet',
  [EventLinkOriginIds.Ai]: 'pink',
  [EventLinkOriginIds.Playbook]: 'teal',
  [EventLinkOriginIds.SourceTag]: 'grape',
};

export const LINK_STATUS_NAMES: Record<number, string> = {
  [EventLinkStatusIds.Suggested]: 'Suggested',
  [EventLinkStatusIds.Approved]: 'Approved',
  [EventLinkStatusIds.Dismissed]: 'Dismissed',
  [EventLinkStatusIds.Removed]: 'Removed',
};

export const SIGNAL_NAMES: Record<number, string> = {
  [EventSignalTypeIds.Tag]: 'Tag',
  [EventSignalTypeIds.TagGroup]: 'Tag group',
  [EventSignalTypeIds.EventType]: 'Event type',
  [EventSignalTypeIds.Campus]: 'Campus',
  [EventSignalTypeIds.Venue]: 'Venue',
  [EventSignalTypeIds.Source]: 'Source',
  [EventSignalTypeIds.TitleKeyword]: 'Event name',
  [EventSignalTypeIds.IsPublic]: 'Public',
  [EventSignalTypeIds.DayOfWeek]: 'Day of the week',
  [EventSignalTypeIds.TimeBand]: 'Time of day',
  [EventSignalTypeIds.AllCampuses]: 'All campuses',
};

/** Operators read as part of a sentence ("Tag is any of Streaming, Worship"). */
export const OPERATOR_LABELS: Record<number, string> = {
  [EventRuleOperatorIds.EqualsTo]: 'is',
  [EventRuleOperatorIds.In]: 'is any of',
  [EventRuleOperatorIds.Contains]: 'contains',
  [EventRuleOperatorIds.NotEquals]: 'is not',
};

export const ACTION_TYPE_NAMES: Record<number, string> = {
  [EventRuleActionTypeIds.AddLink]: 'Add link',
  [EventRuleActionTypeIds.SetCoordinator]: 'Set coordinator',
  [EventRuleActionTypeIds.SuggestPlaybook]: 'Suggest playbook',
};

export const CANDIDATE_STATUS_NAMES: Record<number, string> = {
  [EventRuleCandidateStatusIds.Proposed]: 'Proposed',
  [EventRuleCandidateStatusIds.Accepted]: 'Accepted',
  [EventRuleCandidateStatusIds.Rejected]: 'Rejected',
  [EventRuleCandidateStatusIds.Snoozed]: 'Snoozed',
};

// ---------------------------------------------------------------- rule signals

const O = EventRuleOperatorIds;
const S = EventSignalTypeIds;

/** Operators that make sense for each signal. */
export const SIGNAL_OPERATORS: Record<number, number[]> = {
  [S.Tag]: [O.EqualsTo, O.In, O.NotEquals],
  [S.TagGroup]: [O.EqualsTo, O.In, O.NotEquals],
  [S.EventType]: [O.EqualsTo, O.In, O.NotEquals],
  [S.Campus]: [O.EqualsTo, O.In, O.NotEquals],
  [S.Venue]: [O.EqualsTo, O.NotEquals],
  [S.Source]: [O.EqualsTo, O.NotEquals],
  [S.TitleKeyword]: [O.Contains],
  [S.IsPublic]: [O.EqualsTo],
  [S.DayOfWeek]: [O.EqualsTo, O.In, O.NotEquals],
  [S.TimeBand]: [O.EqualsTo, O.In],
  [S.AllCampuses]: [O.EqualsTo],
};

/** Signals offered in the condition builder, in display order. */
export const SIGNAL_ORDER: number[] = [
  S.Tag,
  S.TagGroup,
  S.EventType,
  S.Campus,
  S.AllCampuses,
  S.Venue,
  S.Source,
  S.TitleKeyword,
  S.IsPublic,
  S.DayOfWeek,
  S.TimeBand,
];

export const DAY_OPTIONS = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

export const TIME_BAND_OPTIONS = [
  { value: 'morning', label: 'Morning (before 12:00)' },
  { value: 'afternoon', label: 'Afternoon (12:00 to 17:00)' },
  { value: 'evening', label: 'Evening (from 17:00)' },
  { value: 'allday', label: 'All day' },
];

export const YES_NO_OPTIONS = [
  { value: 'true', label: 'Yes' },
  { value: 'false', label: 'No' },
];

export const splitValues = (value: string | null | undefined): string[] =>
  (value ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

export const joinValues = (values: string[]): string => values.map((v) => v.trim()).filter(Boolean).join(',');

// ---------------------------------------------------------------- grouping (Connected tab)

export interface LinkGroupNode {
  /** Parent link (department or ministry), or null when only a child is linked. */
  parent: EventLinkView | null;
  parentKey: string;
  parentName: string;
  children: EventLinkView[];
}

export interface GroupedLinks {
  departments: LinkGroupNode[];
  ministries: LinkGroupNode[];
  places: EventLinkView[];
  people: EventLinkView[];
  other: EventLinkView[];
}

const byName = (a: EventLinkView, b: EventLinkView) => (a.targetName ?? a.targetKey).localeCompare(b.targetName ?? b.targetKey);

/** Nest children (designations, teams) under their parent (department, ministry). */
function nest(parents: EventLinkView[], children: EventLinkView[], parentLabel: string): LinkGroupNode[] {
  const nodes = new Map<string, LinkGroupNode>();
  parents
    .slice()
    .sort(byName)
    .forEach((p) => {
      const key = p.targetKey;
      const existing = nodes.get(key);
      if (existing) {
        // Same department linked twice with different roles: keep both by adding a second node.
        nodes.set(`${key}#${p.linkID}`, { parent: p, parentKey: key, parentName: p.targetName || `${parentLabel} ${key}`, children: [] });
      } else {
        nodes.set(key, { parent: p, parentKey: key, parentName: p.targetName || `${parentLabel} ${key}`, children: [] });
      }
    });
  children
    .slice()
    .sort(byName)
    .forEach((c) => {
      const pKey = c.targetParentID != null ? String(c.targetParentID) : `name:${c.targetParentName ?? ''}`;
      let node = nodes.get(pKey);
      if (!node) {
        node = { parent: null, parentKey: pKey, parentName: c.targetParentName || `No ${parentLabel.toLowerCase()}`, children: [] };
        nodes.set(pKey, node);
      }
      node.children.push(c);
    });
  return Array.from(nodes.values()).sort((a, b) => a.parentName.localeCompare(b.parentName));
}

export function groupLinks(links: EventLinkView[]): GroupedLinks {
  const T = EventLinkTypeIds;
  const of = (...types: number[]) => links.filter((l) => types.includes(l.linkTypeID));
  const known = new Set<number>([T.Department, T.Designation, T.Ministry, T.MinistryTeam, T.Venue, T.Location, T.BookableResource, T.Asset, T.Person]);
  return {
    departments: nest(of(T.Department), of(T.Designation), 'Department'),
    ministries: nest(of(T.Ministry), of(T.MinistryTeam), 'Ministry'),
    places: of(T.Venue, T.Location, T.BookableResource, T.Asset).sort(byName),
    people: of(T.Person).sort(byName),
    other: links.filter((l) => !known.has(l.linkTypeID)).sort(byName),
  };
}

/** "Sound (Production)" style label for a link target. */
export function targetLabel(
  l: Pick<EventLinkView, 'targetName' | 'targetKey' | 'targetParentName'> & { linkTypeID?: number },
  withParent = false
): string {
  // Stage 3: campaign (and numeric form) links store their key as the name.
  const raw = l.targetName || '';
  if ((l.linkTypeID === EventLinkTypeIds.Campaign || l.linkTypeID === EventLinkTypeIds.Form) && (!raw || /^\d+$/.test(raw))) {
    return `${l.linkTypeID === EventLinkTypeIds.Campaign ? 'Campaign' : 'Form'} ${raw || l.targetKey}`;
  }
  const name = l.targetName || `#${l.targetKey}`;
  return withParent && l.targetParentName ? `${name} (${l.targetParentName})` : name;
}
